import { Fragment, useState } from "react";

import { StevieFieldRow } from "./StevieFieldRow";
import { StevieMetricCard } from "./StevieMetricCard";
import {
  STEVIE_FIELD_GROUPS_BY_CATALOG,
  STEVIE_FIELD_ICONS,
  STEVIE_ROW_GROUPS_BY_CATALOG,
  stevieFieldLabel,
} from "./stevieFieldCatalog";

import { Column, Row, Text } from "@/components";

import { transcript } from "@/lib/transcript";
import {
  CallStructuredSummary,
  CallStructuredSummaryCatalog,
  CallStructuredSummaryFieldKey,
  CallStructuredSummaryGroup,
} from "@/types";

export const StevieCallBreakdown = ({
  audioRef,
  summary,
}: {
  audioRef: React.RefObject<HTMLAudioElement>;
  summary: CallStructuredSummary;
}) => {
  /* React Hooks */
  const [expandedKey, setExpandedKey] =
    useState<CallStructuredSummaryFieldKey>();

  /* Helpers */
  // Summaries written before the generic catalog existed carry no catalog and
  // are all Bridgetown.
  const catalog = summary.catalog ?? CallStructuredSummaryCatalog.BRIDGETOWN;
  // The status markers grade an answer against what a specific role wants,
  // which stays with the pilot. Generic-catalog calls show the same fields and
  // the same evidence, just without the grading.
  const hideStatusIcon = catalog !== CallStructuredSummaryCatalog.BRIDGETOWN;
  const fieldGroups = STEVIE_FIELD_GROUPS_BY_CATALOG[catalog];
  const fieldsByKey = new Map(
    summary.fields.map((field) => [field.key, field]),
  );
  // A field the call never covered still renders, as a grey N/A. An absent row
  // reads as an oversight, where N/A reads as "asked, nothing there".
  const fieldsForGroup = (group: CallStructuredSummaryGroup) =>
    (fieldGroups[group] ?? [])
      .map((key) => fieldsByKey.get(key))
      .filter((field) => !!field);

  /* Handlers */
  const handleSeek = (timestamp: string) => {
    if (!audioRef.current) return;

    const timeInSeconds = transcript.convertTimestampToSeconds(timestamp);
    if (timeInSeconds === null) return;
    audioRef.current.currentTime = timeInSeconds;
    void audioRef.current.play();
  };

  const handleToggle = (key: CallStructuredSummaryFieldKey) => {
    setExpandedKey(expandedKey === key ? undefined : key);
  };

  /* Render */
  const renderRow = (
    field: NonNullable<ReturnType<typeof fieldsByKey.get>>,
    withIcon: boolean,
  ) => {
    return (
      <Fragment key={field.key}>
        <StevieFieldRow
          field={field}
          hideStatusIcon={hideStatusIcon}
          Icon={withIcon ? STEVIE_FIELD_ICONS[field.key] : undefined}
          isExpanded={expandedKey === field.key}
          label={stevieFieldLabel(catalog, field.key)}
          onSeek={handleSeek}
          onToggle={() => {
            handleToggle(field.key);
          }}
        />
      </Fragment>
    );
  };

  const renderMetricCards = () => {
    const fields = fieldsForGroup(CallStructuredSummaryGroup.QUICK_LOGISTICS);
    if (fields.length === 0) return null;

    return (
      <div className="grid grid-cols-4 gap-3 px-2 pb-2">
        {fields.map((field) => (
          <StevieMetricCard
            field={field}
            hideStatusIcon={hideStatusIcon}
            Icon={STEVIE_FIELD_ICONS[field.key]}
            key={field.key}
            label={stevieFieldLabel(catalog, field.key)}
          />
        ))}
      </div>
    );
  };

  const renderHighlights = () => {
    const fields = fieldsForGroup(CallStructuredSummaryGroup.HIGHLIGHT);
    if (fields.length === 0) return null;

    return fields.map((field) => renderRow(field, true));
  };

  const renderRowGroups = () => {
    return STEVIE_ROW_GROUPS_BY_CATALOG[catalog].map(
      ({ group, Icon, title }) => {
        const fields = fieldsForGroup(group);
        if (fields.length === 0) return null;

        return (
          <div
            className="col-span-2 grid grid-cols-subgrid gap-y-0 pb-4 last:pb-0"
            key={group}
          >
            <Row className="col-span-2 mb-1 items-center gap-3 py-2">
              <Icon
                className="h-4 w-4 shrink-0 text-slate-400"
                strokeWidth={1.5}
              />
              <Text className="font-plex-mono text-[11px] font-medium uppercase tracking-wider text-slate-500">
                {title}
              </Text>
            </Row>
            {fields.map((field) => renderRow(field, false))}
          </div>
        );
      },
    );
  };

  const render = () => {
    if (summary.fields.length === 0) return null;

    return (
      <Column className="gap-5 pt-2">
        {renderMetricCards()}
        <div className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-6 gap-y-2 pl-2">
          {renderHighlights()}
          <div className="col-span-2 grid grid-cols-subgrid gap-y-0">
            {renderRowGroups()}
          </div>
        </div>
      </Column>
    );
  };

  return render();
};
